import React from 'react';
import { Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import backArrow from '../../../assets/images/backarrow.svg';
import BoonLogo from '../../../assets/images/boon-logo.svg';
import BoonViewIcon from '../../../assets/images/BoonViewIcon.svg';
import { FormikTextArea } from '../../../components/FormikTextArea/FormikTextArea';
import NavigationBar from '../../../components/NavigationBar/NavigationBar';
import Notification from '../../../components/Notification/Notification';
import { OpeningSelect } from '../../../components/OpeningSelect/OpeningSelect';
import { OrganizationSelect } from '../../../components/OrganizationSelect/OrganizationSelect';
import { ReferralFlowStepper } from '../../../components/ReferralFlowStepper/ReferralFlowStepper';
import { ReferralProcess } from '../../../components/ReferralProcess/ReferralProcess';
import ButtonWithIcon from '../../../components/shared/ButtonWithIcon/ButtonWithIcon';
import { CandidateInfoSection } from '../../../components/shared/CandidateInfoSection/CandidateInfoSection';
import Header from '../../../components/shared/Header/Header';
import CustomSwitch from '../../../components/shared/Switch/Switch';
import { YourInfoSection } from '../../../components/shared/YourInfoSection/YourInfoSection';
import { useReferralFlowContext } from '../../../contexts/ReferralFlowContext';
import { IsMobile } from '../../../helpers/IsMobile';
import { useCurrentUser } from '../../../hooks/useCurrentUser';
import { useReferralAccess } from '../../../hooks/useReferralAccess';
import { useSubmitReferralMutation } from '../../../redux/apis/referralApi';
import urls from '../../../routing/urls';
import { selectCurrentOrganization } from '../../../selectors/organizationSelectors';

import styles from './Review.module.scss';

const createValidationSchema = (t) =>
  Yup.object({
    messageToCandidate: Yup.string().when('$individualMsgEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required(t('validation:message.toIndividualRequired'))
          .max(300, t('validation:message.maxLength', { max: 300 })),
      otherwise: (schema) => schema.optional(),
    }),
    messageToEmployer: Yup.string().when('$employerMsgEnabled', {
      is: true,
      then: (schema) =>
        schema
          .required(t('validation:message.toEmployerRequired'))
          .max(300, t('validation:message.maxLength', { max: 300 })),
      otherwise: (schema) => schema.optional(),
    }),
  });

export const Review = () => {
  const { t } = useTranslation('referral');
  const validationSchema = createValidationSchema(t);
  const [searchParams, setSearchParams] = useSearchParams();
  const { companyName } = useParams();
  const { isAuthenticated } = useCurrentUser();
  const navigate = useNavigate();
  const {
    jobInfo,
    candidateInfo,
    personalInfo,
    setCurrentStep,
    reviewInfo,
    setReviewInfo,
    generateReferralPayload,
    setJobInfo,
    referralFormPermissions,
  } = useReferralFlowContext();
  const [submitReferral, { isLoading: isSubmitting }] = useSubmitReferralMutation();
  const { strengthenQuestions, accessOrganization } = useReferralFlowContext();
  const { blocked: referralBlocked, message: referralBlockedMessage } = useReferralAccess(accessOrganization);
  const currentOrganization = useSelector(selectCurrentOrganization);
  const isMobile = IsMobile();

  const handleSubmit = (values) => {
    if (referralBlocked) {
      Notification.error(referralBlockedMessage || t('common:referralAccess.referDisabled'));
      return;
    }

    setReviewInfo(values);

    const { resume_files, ...payloadWithoutFiles } = generateReferralPayload();

    const referralFields = {
      ...payloadWithoutFiles,
      is_candidate_message: values.individualMsgEnabled || false,
      candidate_message: values.individualMsgEnabled ? values.messageToCandidate : '',
      is_employer_message: values.employerMsgEnabled || false,
      employer_message: values.employerMsgEnabled ? values.messageToEmployer : '',
    };

    let referralPayload;
    if (resume_files && resume_files.length > 0) {
      const formData = new FormData();
      Object.entries(referralFields).forEach(([key, value]) => {
        if (key === 'referral_form_custom_questions') {
          formData.append(key, JSON.stringify(value));
        } else if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
      resume_files.forEach((file) => {
        formData.append('pdf_files[]', file);
      });
      referralPayload = formData;
    } else {
      referralPayload = referralFields;
    }

    submitReferral(referralPayload)
      .unwrap()
      .then((response) => {
        if (response.error) {
          Notification.error(response.error);
          return;
        }

        if (strengthenQuestions.enable_referral_questions) {
          if (companyName) {
            navigate(
              urls.companyReferralStrengthen
                .replace(':companyName', companyName)
                .replace(':referralId', response.payload.refer_request.uuid)
            );
          } else {
            navigate(urls.referralFlowStrengthen.replace(':referralId', response.payload.refer_request.uuid));
          }
        } else if (companyName) {
          navigate(
            urls.companyReferralSuccess
              .replace(':companyName', companyName)
              .replace(':referralId', response.payload.refer_request.uuid)
          );
        } else {
          navigate(urls.referralFlowSuccess.replace(':referralId', response.payload.refer_request.uuid));
        }
      })
      .catch((error) => {
        const apiMessage = error?.data?.error || error?.data?.message;
        Notification.error(apiMessage || t('common:errors.somethingWentWrong'));
      });
  };

  return (
    <div className="flex w-full">
      {isAuthenticated && !companyName && <NavigationBar />}
      <div className={styles.outerContainer}>
        <Header
          isPublic={!isAuthenticated || companyName}
          title={t('headerTitle')}
          backButton={isAuthenticated && !companyName ? t('common:back') : ''}
          logo={isMobile ? BoonViewIcon : BoonLogo}
          logoClassName={isMobile ? 'w-full h-full' : 'w-full h-[20px] rounded-none'}
        />
        <div className={styles.mainContainer}>
          <ReferralProcess />
          <div className={styles.container}>
            <ReferralFlowStepper currentStep={4} />

            <div className={styles.formSection}>
              <div className={styles.formHeader}>
                <h2>{t('sendAReferral')}</h2>
              </div>

              <div className={styles.previousInfo}>
                <YourInfoSection personalInfo={personalInfo} setCurrentStep={setCurrentStep} />
                <CandidateInfoSection
                  candidateInfo={candidateInfo}
                  setCurrentStep={setCurrentStep}
                  customQuestions={referralFormPermissions.referral_form_custom_questions || []}
                />
                {!isAuthenticated && !companyName && (
                  <div className="space-y-[6px]">
                    <label htmlFor="company" className={styles.formLabel}>
                      {t('company')}
                    </label>
                    <OrganizationSelect value={jobInfo.company} isDisabled />
                  </div>
                )}
                <div className="space-y-[6px]">
                  <label htmlFor="opening" className={styles.formLabel}>
                    {t('openingInfo')}
                  </label>
                  <OpeningSelect
                    organizationId={jobInfo.company}
                    value={jobInfo.opening}
                    onChange={(selectedOption) => {
                      setJobInfo((prev) => ({ ...prev, opening: selectedOption.value }));
                      searchParams.delete('refer_for');
                      setSearchParams(searchParams);
                    }}
                  />
                </div>
              </div>

              <Formik
                initialValues={{
                  individualMsgEnabled:
                    reviewInfo?.individualMsgEnabled ?? referralFormPermissions?.show_message_to_candidate ?? true,
                  employerMsgEnabled:
                    reviewInfo?.employerMsgEnabled ?? referralFormPermissions?.show_message_to_employer ?? true,
                  messageToCandidate:
                    reviewInfo?.messageToCandidate ||
                    t('reviewPage.defaultCandidateMessage', { organization: currentOrganization?.name || 'Boon' }),
                  messageToEmployer: reviewInfo?.messageToEmployer || t('reviewPage.defaultEmployerMessage'),
                }}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ setFieldValue, values }) => (
                  <Form>
                    <div className={styles.form}>
                      {referralFormPermissions?.show_message_to_candidate && (
                        <div>
                          <div
                            className={styles.formLabel}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '6px',
                            }}
                          >
                            <label htmlFor="messageToCandidate">{t('reviewPage.messageToIndividual')}</label>
                            <CustomSwitch
                              checked={values.individualMsgEnabled}
                              onChange={() => setFieldValue('individualMsgEnabled', !values.individualMsgEnabled)}
                            />
                          </div>
                          {values.individualMsgEnabled && (
                            <FormikTextArea
                              name="messageToCandidate"
                              label={t('reviewPage.messageToIndividual')}
                              placeholder={t('reviewPage.defaultCandidateMessage', {
                                organization: currentOrganization?.name || 'Boon',
                              })}
                              maxLength={300}
                              shouldDisplayLabel={false}
                              showCharacterCount
                              description={t('reviewPage.defaultNote')}
                              rows={4}
                            />
                          )}
                        </div>
                      )}
                      {referralFormPermissions?.show_message_to_employer && (
                        <div>
                          <div
                            className={styles.formLabel}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '6px',
                            }}
                          >
                            <label htmlFor="messageToEmployer">{t('reviewPage.messageToEmployer')}</label>
                            <CustomSwitch
                              checked={values.employerMsgEnabled}
                              onChange={() => setFieldValue('employerMsgEnabled', !values.employerMsgEnabled)}
                            />
                          </div>
                          {values.employerMsgEnabled && (
                            <FormikTextArea
                              name="messageToEmployer"
                              label={t('reviewPage.messageToEmployer')}
                              placeholder={t('common:placeholders.defaultReferralMessage')}
                              maxLength={300}
                              shouldDisplayLabel={false}
                              showCharacterCount
                              description={t('reviewPage.defaultNote')}
                            />
                          )}
                        </div>
                      )}
                    </div>
                    <div className={styles.nextButtonContainer}>
                      <ButtonWithIcon
                        img={backArrow}
                        type="button"
                        variant="tertiary-36"
                        text={t('common:back')}
                        textClassName="text-[#314155]"
                        containerClass="w-[142px]"
                        onClick={() => setCurrentStep(3)}
                        disabled={isSubmitting}
                      />
                      <ButtonWithIcon
                        data-testid="submit-referral-button"
                        type="submit"
                        variant="primary-36"
                        text={
                          <span className="flex items-center gap-[8px]">
                            {isSubmitting && <Spinner animation="border" size="sm" />} {t('reviewPage.submitReferral')}
                          </span>
                        }
                        textClassName="text-white"
                        containerClass="min-w-[142px]"
                        disabled={isSubmitting}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
